import { _T } from './Localization';
import { JudgingType, RoundType } from "@vote4dance/judging-schema";
import { buildRoundSubmitValidationFromThrough, safeParseRoundSubmitValidation } from "../models/rounds";
export class RoundGenerator {
    constructor(classes, rules, dances, judging) {
        this.roundSizes = [6, 12, 24, 48, 96, 192];
        this.roundNames = _T('classrules.details.round_label.default').split(',').map(a => a.trim());
        this.classes = classes;
        this.rules = rules;
        this.dances = dances;
        this.judging = judging;
    }
    generateRounds(participantCount, tags) {
        let allRounds = [];
        for (const federationClass of this.classes) {
            let rounds = [];
            let rules = [];
            const ruleTagCheck = (rule) => {
                const ruleTags = rule.class_rule_tags ? JSON.parse(rule.class_rule_tags) : [];
                if (ruleTags.length === 0) {
                    return true;
                }
                return tags.some(element => ruleTags.includes(element));
            };
            // Filter rules for the current class
            const classRules = this.rules.filter((rule) => rule.class_rule_enabled &&
                ruleTagCheck(rule) &&
                rule.federation_id === federationClass.federation_id &&
                rule.discipline_id === federationClass.discipline_id &&
                (rule.category_id === federationClass.category_id || rule.category_id === null) &&
                (rule.class_level_id === federationClass.class_level_id || rule.class_level_id === null) &&
                (rule.age_group_id === federationClass.age_group_id || rule.age_group_id === null));
            // Determine round sizes and names
            this.roundSizes = this.determineRoundSizes(classRules, participantCount);
            this.roundNames = this.determineRoundNames(classRules, participantCount);
            // Sort round sizes in ascending order
            this.roundSizes.sort((a, b) => a - b);
            // Find the closest round size to the number of participants
            let startRoundSizeIndex = this.roundSizes.findIndex((size) => size >= participantCount);
            if (startRoundSizeIndex === -1) {
                // If no round size is greater than or equal to the number of participants, start with the largest available round size
                startRoundSizeIndex = this.roundSizes.length - 1;
            }
            // Iterate through round sizes to create rounds 
            let createOrder = 0;
            let numberOfAddedRounds = 0;
            let incrementNameIndex = 0;
            for (let i = startRoundSizeIndex; i >= 0; i--) {
                createOrder++;
                const roundSize = this.roundSizes[i];
                let numThrough = i > 0 ? this.roundSizes[i - 1] : 0;
                let roundName = i >= this.roundNames.length ? this.roundNames[this.roundNames.length - 1] : this.roundNames[i];
                if (roundName.includes('[X]')) {
                    incrementNameIndex++;
                    if (this.roundNames[i - 1] === roundName || incrementNameIndex > 1) {
                        roundName = roundName.replace('[X]', incrementNameIndex.toString()).trim();
                    }
                    else {
                        roundName = roundName.replace('[X]', '').trim();
                    }
                }
                let preliminaryJudging = this.judging.find(a => a.judging_type === JudgingType.Select);
                let finalJudging = this.judging.find(a => a.judging_type === JudgingType.Placement);
                let updateRound = {
                    round_label: roundName,
                    round_count: createOrder === 1 ? participantCount : roundSize,
                    judging_id: numThrough > 0 ? preliminaryJudging === null || preliminaryJudging === void 0 ? void 0 : preliminaryJudging.judging_id : finalJudging === null || finalJudging === void 0 ? void 0 : finalJudging.judging_id,
                    judging_label: numThrough > 0 ? preliminaryJudging === null || preliminaryJudging === void 0 ? void 0 : preliminaryJudging.judging_label : finalJudging === null || finalJudging === void 0 ? void 0 : finalJudging.judging_label,
                    round_num_through: numThrough,
                    round_num_heats: this.determineHeatCount(createOrder === 1 ? participantCount : roundSize, 0),
                    round_type: 'normal',
                    round_weight: 1
                };
                rounds.push(updateRound);
                // Loop through all rules and apply/print only if conditions are met
                for (const rule of classRules) {
                    let previousRound = updateRound;
                    // Check if we have a rule with round 0, presentation etc
                    if (createOrder === 1) {
                        if (this.evaluateConditions(participantCount, 0, i, rule.class_rule_conditions)) {
                            if (!rules.includes(rule)) {
                                rules.push(rule);
                            }
                            if (rule.class_rule_action && rule.class_rule_action === 'add') {
                                numberOfAddedRounds++;
                                let addedRound = {
                                    ...updateRound
                                };
                                for (const otherRule of classRules) {
                                    if (rule != otherRule && this.evaluateConditions(participantCount, 0, i, otherRule.class_rule_conditions)) {
                                        this.applyRuleChanges(addedRound, null, otherRule.class_rule_changes);
                                    }
                                }
                                this.applyRuleChanges(addedRound, null, rule.class_rule_changes);
                                this.updateDynamicRoundFields(addedRound);
                                const dances = this.generateSubDances(addedRound, rounds.length, rule);
                                addedRound.children = dances;
                                numberOfAddedRounds += dances.length;
                                rounds.unshift(addedRound);
                                continue;
                            }
                        }
                    }
                    // Adjust the comparison based on direction
                    if (this.evaluateConditions(participantCount, createOrder, i, rule.class_rule_conditions)) {
                        if (!rules.includes(rule)) {
                            rules.push(rule);
                        }
                        // Action - add, modify, remove
                        let action = 'modify';
                        if (rule.class_rule_action && rule.class_rule_action !== null) {
                            action = rule.class_rule_action;
                        }
                        if (action === 'add') {
                            numberOfAddedRounds++;
                            let addedRound = {
                                ...updateRound,
                                children: undefined,
                                round_count: previousRound.round_count - previousRound.round_num_through,
                                round_num_heats: this.determineHeatCount(previousRound.round_count - previousRound.round_num_through, 0)
                            };
                            this.applyRuleChanges(addedRound, previousRound, rule.class_rule_changes);
                            if (addedRound.round_num_through > 0) {
                                // Update previous round num through
                                previousRound.round_num_through = previousRound.round_num_through - addedRound.round_num_through;
                                // Update count based on previous round through
                                addedRound.round_position = previousRound.round_num_through + 1;
                                addedRound.round_count = updateRound.round_count - updateRound.round_num_through;
                            }
                            for (const otherRule of classRules) {
                                if (rule != otherRule && this.evaluateConditions(participantCount, createOrder + numberOfAddedRounds, i, otherRule.class_rule_conditions)) {
                                    this.applyRuleChanges(addedRound, previousRound, otherRule.class_rule_changes);
                                }
                            }
                            const dances = this.generateSubDances(addedRound, rounds.length, rule);
                            if (dances.length > 0) {
                                addedRound.children = dances;
                            }
                            this.updateDynamicRoundFields(addedRound);
                            numberOfAddedRounds += dances.length;
                            rounds.push(addedRound);
                            continue;
                        }
                        if (action === 'remove') {
                            rounds.pop();
                            continue;
                        }
                        this.applyRuleChanges(updateRound, previousRound, rule.class_rule_changes);
                        const dances = this.generateSubDances(updateRound, rounds.length + numberOfAddedRounds - 1, rule);
                        numberOfAddedRounds += dances.length;
                        if (dances.length > 0) {
                            updateRound.children = dances;
                        }
                    }
                }
                // All parent rounds should have 1 heat only
                if (updateRound.children &&
                    updateRound.children.length > 0 &&
                    updateRound.children.every((child) => child.round_type === RoundType.Normal)) {
                    updateRound.round_num_heats = 1;
                }
                this.updateDynamicRoundFields(updateRound);
            }
            allRounds.push({
                federation_class_id: federationClass.federation_class_id,
                rules: rules,
                rounds: rounds,
            });
        }
        return allRounds;
    }
    getJudgingSettingsById(judgingId) {
        const foundJudging = this.judging.find((j) => j && (j.judging_id === judgingId || j.id === judgingId));
        if (!foundJudging)
            return undefined;
        const rawSettings = foundJudging.judging_settings;
        if (!rawSettings)
            return undefined;
        try {
            return typeof rawSettings === 'string' ? JSON.parse(rawSettings) : rawSettings;
        }
        catch (e) {
            console.warn('Invalid judging_settings for judging', judgingId, e);
            return undefined;
        }
    }
    updateDynamicRoundFields(round) {
        if (!round)
            return;
        const hasInheritPlaceholder = (val) => {
            if (val == null)
                return false;
            if (typeof val === 'string')
                return val.includes('[INHERIT]');
            if (typeof val === 'object') {
                const parsed = safeParseRoundSubmitValidation(val);
                if (parsed && typeof parsed === 'object') {
                    const abs = parsed.absolute;
                    return typeof abs === 'string' && abs.includes('[INHERIT]');
                }
            }
            return false;
        };
        const updaters = [
            () => {
                if (!hasInheritPlaceholder(round.round_submit_validation))
                    return;
                const judgingSettings = this.getJudgingSettingsById(round.judging_id);
                const computed = buildRoundSubmitValidationFromThrough(round.round_num_through, judgingSettings, null);
                round.round_submit_validation = computed || null;
            },
        ];
        updaters.forEach(fn => fn());
    }
    getChangeWithKey(changes, key) {
        var _a;
        return (_a = changes === null || changes === void 0 ? void 0 : changes.filter(a => a != null).find(a => a.key === key)) === null || _a === void 0 ? void 0 : _a.value;
    }
    applyRuleChanges(updateRound, previousRound, input) {
        const changes = input ? JSON.parse(input) : [];
        // Check if any of the specified properties have changed
        for (const change of changes) {
            if (change !== undefined && change !== null) {
                const prop = change.key;
                switch (prop) {
                    case 'dances':
                    case 'round_sizes':
                        break;
                    case 'round_label':
                        let nameList = change.value;
                        if (nameList.length === 1) {
                            updateRound[prop] = nameList[0];
                        }
                        break;
                    case 'round_type':
                        updateRound[prop] = (change.value && ['chance', 'points'].includes(change.value)) ? change.value : 'normal';
                        break;
                    case 'music_length':
                    case 'extra_time':
                        const musicLength = this.getChangeWithKey(changes, 'music_length') || 0;
                        const extraTime = this.getChangeWithKey(changes, 'extra_time') || 0;
                        updateRound['round_heat_length'] = musicLength + extraTime;
                        break;
                    case 'max_floor':
                        updateRound['round_num_heats'] = this.determineHeatCount(updateRound.round_count, change.value);
                        break;
                    case 'round_num_through':
                        const numThrough = change.value;
                        const throughType = change.extra;
                        const ruleFactor = numThrough !== undefined ? (numThrough / 100) : 0.5;
                        const roundNumThrough = (numThrough !== undefined &&
                            (throughType == 'percent' || throughType == null)) ?
                            Math.round((previousRound ? previousRound.round_num_through : updateRound.round_count) * ruleFactor)
                            :
                                (numThrough || 0);
                        updateRound[prop] = roundNumThrough;
                        break;
                    case 'round_hide_results_pos':
                        updateRound[prop] = change.value;
                        updateRound['round_hide_results_type'] = change.extra;
                        break;
                    case 'judging_id':
                        updateRound[prop] = change.value;
                        const foundJudging = this.judging.find(a => a.judging_id === change.value);
                        updateRound['judging_label'] = foundJudging === null || foundJudging === void 0 ? void 0 : foundJudging.judging_label;
                        break;
                    default:
                        updateRound[prop] = change.value;
                }
            }
        }
    }
    generateSubDances(updateRound, round_parent_id, rule) {
        const changes = rule.class_rule_changes ? JSON.parse(rule.class_rule_changes) : [];
        const dancesValues = this.getChangeWithKey(changes, 'dances');
        let dances = [];
        let list = dancesValues || [];
        for (const danceId of list) {
            const foundDance = this.dances.find(a => a.dance_id === danceId);
            if (foundDance) {
                const roundType = this.getChangeWithKey(changes, 'round_type');
                if (roundType && roundType == 'battle') {
                    // Battle type
                    const roundCount = Math.ceil(updateRound.round_count / 2);
                    for (let index = 0; index < roundCount; index++) {
                        const danceChanges = foundDance.dance_changes ? JSON.parse(foundDance.dance_changes) : [];
                        const maxFloor = this.getChangeWithKey(danceChanges, 'max_floor');
                        const dance = {
                            ...updateRound,
                            round_label: foundDance.dance_name.replace('[X]', (index + 1).toString()),
                            round_parent_id: round_parent_id,
                            round_num_heats: this.determineHeatCount(2, maxFloor),
                            round_num_through: 1,
                            round_count: 2
                        };
                        this.applyRuleChanges(dance, null, foundDance.dance_changes);
                        this.updateDynamicRoundFields(dance);
                        dances.push(dance);
                    }
                }
                else {
                    // Normal round
                    let dance = {
                        ...updateRound,
                        round_label: foundDance.dance_name,
                        round_parent_id: round_parent_id
                    };
                    this.applyRuleChanges(dance, null, foundDance.dance_changes);
                    this.updateDynamicRoundFields(dance);
                    dances.push(dance);
                }
            }
        }
        return dances;
    }
    determineRoundSizes(rules, participantCount) {
        const pairs = [];
        for (const rule of rules) {
            let changes = [];
            try {
                changes = rule.class_rule_changes ? JSON.parse(rule.class_rule_changes) : [];
            }
            catch {
                changes = [];
            }
            const allRoundSizes = changes === null || changes === void 0 ? void 0 : changes.filter((a) => a != null && a.key === 'round_sizes').map((a) => a.value);
            const roundSizes = allRoundSizes && allRoundSizes.length > 0 ? allRoundSizes[0] : null; // base sizes
            const marginRoundSizes = allRoundSizes && allRoundSizes.length > 1 ? allRoundSizes[1] : null; // margin sizes
            // Only consider rules whose conditions pass
            const conditionsOk = (rule.class_rule_conditions !== null)
                ? this.evaluateConditions(participantCount, -1, -1, rule.class_rule_conditions)
                : true;
            if (!conditionsOk || !roundSizes)
                continue;
            const bases = (Array.isArray(roundSizes) ? roundSizes : [roundSizes])
                .map((s) => parseInt(String(s), 10))
                .filter((n) => Number.isFinite(n) && n > 0);
            const margins = marginRoundSizes && Array.isArray(marginRoundSizes)
                ? marginRoundSizes.map((s) => {
                    const n = parseInt(String(s), 10);
                    return Number.isFinite(n) && n > 0 ? n : undefined;
                })
                : [];
            for (let i = 0; i < bases.length; i++) {
                const base = bases[i];
                const margin = margins[i];
                pairs.push({ base, margin: (margin && margin > base) ? margin : undefined });
            }
        }
        // Fallback to default sizes if no rules provided valid sizes
        if (pairs.length === 0)
            return this.roundSizes;
        // Start with the base ladder
        const effective = Array.from(new Set(pairs.map(p => p.base))).sort((a, b) => a - b);
        // If participants are within a (base, margin] window, replace that base with the margin (skip the next larger round)
        for (const { base, margin } of pairs) {
            if (margin && participantCount > base && participantCount <= margin) {
                const idx = effective.indexOf(base);
                if (idx !== -1) {
                    effective[idx] = margin;
                    break; // only one window should apply
                }
            }
        }
        // Ensure ascending unique sizes after replacement
        return Array.from(new Set(effective)).sort((a, b) => a - b);
    }
    determineRoundNames(rules, participantCount) {
        const names = [];
        // Iterate through each rule and collect round names
        for (const rule of rules) {
            const changes = rule.class_rule_changes ? JSON.parse(rule.class_rule_changes) : [];
            const roundLabel = this.getChangeWithKey(changes, 'round_label');
            if (roundLabel !== null &&
                (rule.class_rule_conditions !== null ? this.evaluateConditions(participantCount, -1, -1, rule.class_rule_conditions) : true) &&
                rule.class_rule_action === null &&
                roundLabel) {
                names.push(...roundLabel);
            }
        }
        return names.length > 0 ? names : this.roundNames;
    }
    determineHeatCount(teams, floor) {
        if (!floor || floor == 0) {
            return Math.ceil(teams / this.roundSizes[0]);
        }
        return Math.ceil(teams / floor);
    }
    evaluateConditions(participantCount, createOrder, roundIndex, conditions) {
        const conditionList = conditions ? JSON.parse(conditions) : [];
        return conditionList.every(condition => {
            if (condition.type === 'round') {
                let searchNumber = condition.number != null ? condition.number : 1;
                let adjustedOrder = condition.direction === 'end' ? roundIndex + 1 : createOrder;
                const isValidRound = this.evaluateOperator(searchNumber, adjustedOrder, condition.operator);
                return isValidRound;
            }
            else {
                let searchNumber = condition.number || 1;
                const isValidTeamLimit = this.evaluateOperator(participantCount, searchNumber, condition.operator);
                return isValidTeamLimit;
            }
        });
    }
    evaluateOperator(first, second, operator) {
        switch (operator) {
            case "<":
                return first < second;
            case "<=":
                return first <= second;
            case "==":
                return first === second;
            case ">=":
                return first >= second;
            case ">":
                return first > second;
            case "!=":
                return first !== second;
            default:
                return true;
        }
    }
}
